import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
} from '@angular/core';
import { BillingManagementService } from '../../../service/billing-management.service';
import {
  BillingDetailsSimple,
  InteractionType,
  KeyValueSet,
  LocalService,
  PrinterNamesWithReceiptTemplatesRes,
  ReceiptOption,
  ReceiptTemplateRes,
  Status,
} from '../../../service/local.service';
import {
  KeyValueId,
  PrepareHtmlWithValues,
} from 'src/app/supporting/receiptPrint';
import { OfflineService } from 'src/service/offline.service';
import { LanguageManagementService } from 'src/service/language-management.service';
import { PrintContent, PrintData } from 'src/app/entities/offline';

@Component({
  selector: 'app-print-receipt',
  templateUrl: './print-receipt.component.html',
  styleUrls: ['./print-receipt.component.scss'],
})
export class PrintReceiptComponent implements OnInit {
  @Input() IsSample = false;
  @Input() receiptId = '';
  @Input() printingLanguageId = '';
  @Input() isForBusiness = false;
  @Input() businessId = '';
  @Input() interactionType: InteractionType = InteractionType.None;
  @Input() isManualPrint = false;

  manualPrintersSource: KeyValueSet[] = [];
  manualPrinters: KeyValueSet[] = [];
  searchKey = '';
  nextPrintIndex = 0;

  @Input() selectedReceiptOptionsForPrint: ReceiptOption[] = [];
  @Input() receiptType: number | string = 0;
  FileName = 'print';
  @Output() onPrinted = new EventEmitter();

  lstSectionConfigKeys: string[] = ['common', 'table', 'printer'];
  maxBodyHeight = 0;
  elementIds: any = {
    qr_code_container: 'qr_code_container',
    txt_search_key: 'txt_search_key_print_receipt',
  };

  languageCode = '';
  ctryCode = '';
  isPrintReceipt = false;
  printContent: string = '';
  billedItems: BillingDetailsSimple;
  printContents: KeyValueId[] = [];
  isShowPreview = false;
  mergeGap: number = 10;

  constructor(
    public localService: LocalService,
    private billingManagementService: BillingManagementService,
    private offlineService: OfflineService,
    private languageManagementService: LanguageManagementService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.languageManagementService.ensureLanguageSectionKeys(
      this.lstSectionConfigKeys,
    );
    this.ctryCode = this.localService.selected.countryCode;
    this.onSetLanguageCode();
    this.onLoad();
  }

  ngAfterViewChecked() {
    this.maxBodyHeight = this.localService.onGetCustomBodyHeight();
    this.cdr.detectChanges();
  }

  onSetLanguageCode() {
    if (this.printingLanguageId == '') {
      this.printingLanguageId = this.localService.selected.applicationLanguage;
    }
    this.languageCode = this.localService.getLanguageCode(
      this.printingLanguageId,
    );
  }

  printerTemplates: ReceiptTemplateRes[] = [];
  printerNames: PrinterNamesWithReceiptTemplatesRes[] = [];

  onLoad() {
    let currentDevice =
      this.localService.onGetCurrentDeviceSettingsOfBusiness();

    this.printerTemplates =
      this.localService.loggedMember.receiptTemplates.filter(
        (x) => x.businessId == this.businessId,
      );

    this.printerNames =
      this.localService.loggedMember.printerNamesWithReceiptTemplates.filter(
        (x) => x.businessId == this.businessId,
      );

    if (currentDevice.IsEnableSilentPrint && this.printerNames.length == 0) {
      this.offSilentPrinter();
      currentDevice = this.localService.onGetCurrentDeviceSettingsOfBusiness();
    }

    if (currentDevice.IsEnableSilentPrint) {
      let currentPrinterCount = 0;

      for (const printerName of this.printerNames) {
        this.offlineService
          .isServiceAvailable(printerName.networkUrl)
          .subscribe(
            (result) => {
              currentPrinterCount++;
              if (result.status != Status.Success) {
                this.offSilentPrinter();
              }
              this.onCheckInitiateTemplate(currentPrinterCount);
            },
            (error) => {
              currentPrinterCount++;
              this.offSilentPrinter();
              this.onCheckInitiateTemplate(currentPrinterCount);
            },
          );
      }
    } else {
      this.onInitiateTemplates(currentDevice.IsEnableSilentPrint);
    }
  }

  offSilentPrinter() {
    this.localService.loggedMember.deviceSettings
      .filter((x) => x.BusinessId == this.businessId)
      .forEach((x) => {
        x.IsEnableSilentPrint = false;
      });
  }

  onCheckInitiateTemplate(currentPrinterCount: number) {
    const totalPrinters = this.printerNames.length;
    if (currentPrinterCount == totalPrinters) {
      const currentDevice =
        this.localService.onGetCurrentDeviceSettingsOfBusiness();
      this.onInitiateTemplates(currentDevice.IsEnableSilentPrint);
    }
  }

  onInitiateTemplates(isEnableSilentPrint: boolean) {
    if (isEnableSilentPrint) {
      this.printerNames.forEach((printerName) => {
        printerName.receiptTemplateIds.forEach((receiptTemplateId) => {
          const template = this.printerTemplates.find(
            (x) => x.id == receiptTemplateId,
          );
          if (template) {
            this.manualPrintersSource.push({
              key: printerName.id,
              value: [
                printerName.networkName,
                printerName.printerName,
                template.title,
              ].join('/'),
              selectedValueKey: template.id,
            } as KeyValueSet);
          }
        });
      });
    } else {
      this.printerTemplates.forEach((template) => {
        this.manualPrintersSource.push({
          key: template.id,
          value: template.title,
          selectedValueKey: template.id,
        } as KeyValueSet);
      });
    }
    this.ensureTemplateHtmlCache();
  }

  onShowPrint() {
    const currentDevice =
      this.localService.onGetCurrentDeviceSettingsOfBusiness();
    if (currentDevice.IsEnableSilentPrint) {
      let printerCount = 0;
      for (const printerName of this.printerNames) {
        printerCount++;

        let receiptCount = 0;
        for (const receiptTemplateId of printerName.receiptTemplateIds) {
          receiptCount++;

          let content = this.onGetHtmlByTemplateId(receiptTemplateId);
          const payload = {
            values: [
              {
                htmlContent: content,
                printerNames: [printerName.printerName],
                templateId: receiptTemplateId,
              } as PrintContent,
            ],
            isDirect: true,
            hostedUrl: printerName.networkUrl,
            tempFolder: printerName.temporaryFolderPath,
          } as PrintData;

          this.offlineService
            .setPrint(printerName.networkUrl, payload)
            .subscribe((result) => {
              if (
                printerCount == this.printerNames.length &&
                receiptCount == printerName.receiptTemplateIds.length
              ) {
                this.onPrinted.emit(true);
              }
            });

          if (
            printerCount == this.printerNames.length &&
            receiptCount == printerName.receiptTemplateIds.length
          ) {
            this.onPrinted.emit(true);
          }
        }
      }
    } else {
      if (this.printerTemplates.length > 1) {
        this.onFilter();
        this.isManualPrint = true;
      } else {
        if (this.printerTemplates.length > 0) {
          this.onBrowserPrint(this.printerTemplates[0].id);
        }
      }
    }
  }

  onBrowserPrint(templateId: string) {
    this.printContent = this.onGetHtmlByTemplateId(templateId);
    this.isPrintReceipt = true;
  }

  onPreview(templateId: string) {
    this.printContent = this.onGetHtmlByTemplateId(templateId);
    this.isShowPreview = true;
  }

  onGetHtmlByTemplateId(templateId: string): string {
    const html =
      this.printContents.find((x) => x.key == templateId)?.value ?? '';
    return html;
  }

  onEnsureItems() {
    if (this.billedItems) {
      this.onGenerateHtmls();
    } else {
      const isFixed =
        this.localService.receiptPrintDatas.filter(
          (x) =>
            x.businessForPrinting.businessId == this.businessId && x.isFixed,
        ).length > 0;

      const selectedOptions =
        this.localService.receiptPrintDatas
          .find((x) => x.businessForPrinting.businessId == this.businessId)
          ?.businessForPrinting?.selectedReceiptOptionsForPrint.map((x) => x) ??
        [];

      const isGetCustomLabels =
        isFixed ||
        selectedOptions.filter((x) =>
          [
            ReceiptOption.CustomLabelTop,
            ReceiptOption.CustomLabelBottom,
          ].includes(x),
        ).length > 0;

      this.billingManagementService
        .getBilledProducts(
          this.receiptId,
          this.printingLanguageId,
          true,
          true,
          true,
          true,
          isGetCustomLabels,
          isFixed,
        )
        .subscribe((result) => {
          this.billedItems = result;
          this.onGenerateHtmls();
        });
    }
  }

  onGenerateHtmls() {
    this.printContents = [];
    this.printerTemplates.forEach((template) => {
      let printHtmlData = this.localService.receiptPrintDatas.find(
        (x) =>
          x.printerTemplateId == template.id &&
          x.languageCode == this.languageCode,
      );
      if (printHtmlData) {
        printHtmlData.localDateTimeWithFormat =
          this.localService.getDeviceDateWithFormat();
        printHtmlData.localDateWithFormat =
          this.localService.getDeviceDateWithFormat('DD-MMM-YYYY');
        printHtmlData.localTimeWithFormat =
          this.localService.getDeviceDateWithFormat('hh:mm A');
        printHtmlData.billedItems = this.billedItems;

        this.printContents.push({
          key: template.id,
          value: PrepareHtmlWithValues(printHtmlData),
        });
      }
    });
    this.onShowPrint();
  }

  onFilter() {
    const searchKey = this.searchKey.trim().toLowerCase();
    if (searchKey) {
      this.manualPrinters = this.manualPrintersSource.filter(
        (x) =>
          x.value.toLowerCase().indexOf(searchKey) > -1 ||
          x.s_value.toLowerCase().indexOf(searchKey) > -1,
      );
    } else {
      this.manualPrinters = this.manualPrintersSource;
    }
  }

  ensureTemplateHtmlCache() {
    const templateIds = this.printerTemplates.map((x) => x.id);
    const foundTemplateCaches = this.localService.receiptPrintDatas
      .filter(
        (x) =>
          x.businessForPrinting.businessId == this.businessId &&
          x.languageCode == this.languageCode &&
          templateIds.includes(x.printerTemplateId),
      )
      .map((x) => x.printerTemplateId);

    const notFoundTemplateCaches = templateIds.filter(
      (x) => !foundTemplateCaches.includes(x),
    );

    if (notFoundTemplateCaches.length > 0) {
      this.billingManagementService
        .getReceiptPrintLocalByTemplateIds(
          this.businessId,
          templateIds,
          this.languageCode,
        )
        .subscribe((result) => {
          result.forEach((data) => {
            this.localService.receiptPrintDatas.push(data);
          });
          this.onEnsureItems();
        });
    } else {
      this.onEnsureItems();
    }
  }

  onPrintedReceipt() {
    if (this.manualPrintersSource.length == 1) {
      this.onPrinted.emit();
    } else {
      this.isPrintReceipt = false;
    }
  }

  onAction(index: number) {
    switch (index) {
      case 1:
        {
          this.onPrinted.emit();
        }
        break;
    }
  }

  onMergePreview(isPrint: boolean = false) {
    const selectedItems = this.manualPrinters.filter((x) => x.isMerge);
    if (selectedItems.length > 0) {
      const templateIds = selectedItems
        .filter((x) => x.mergeOrder && x.mergeOrder.length > 0)
        .sort((a, b) => a.mergeOrder.localeCompare(b.mergeOrder))
        .map((x) => x.key);

      let printContentsTemp = this.printContents.filter((x) =>
        templateIds.includes(x.key),
      );
      let printContents: string[] = [];

      for (let i = 0; i < templateIds.length; i++) {
        let printContent = printContentsTemp.find(
          (x) => x.key == templateIds[i],
        ).value;
        printContent = this.replaceAll(
          printContent,
          'id="originalContent"',
          'id="originalContent" style="margin-bottom:' + this.mergeGap + 'px"',
        );
        printContents.push(printContent);
      }

      this.printContent = printContents.join('');
      if (isPrint) {
        this.isPrintReceipt = true;
      } else {
        this.isShowPreview = true;
      }
    }
  }

  replaceAll = (
    fullValue: string,
    toReplaceValue: string = '',
    replaceWith: string = '',
  ): string => {
    if (fullValue) {
      var regExp = new RegExp(toReplaceValue, 'g');
      var newStr = fullValue.replace(regExp, replaceWith);
      return newStr;
    } else {
      return '';
    }
  };

  onSetMergeOrder(key: string) {
    const lastMergeNumbers = this.manualPrinters
      .filter((x) => x.mergeOrder && x.mergeOrder.length > 0)
      .sort((a, b) => a.mergeOrder.localeCompare(b.mergeOrder));

    const nextMergeNumber =
      lastMergeNumbers.length > 0
        ? parseInt(lastMergeNumbers[lastMergeNumbers.length - 1].mergeOrder) + 1
        : 1;

    let item = this.manualPrinters.find((x) => x.key == key);
    if (item) {
      item.mergeOrder = item.isMerge ? nextMergeNumber.toString() : '0';
    }
  }
}
